/**
 * Project progress from completion steps (preferred) or tasks, with safe fallbacks.
 * Used by ProjectStatusGraphic and ProjectNavCard so behavior and edge handling stay consistent.
 *
 * Edge handling:
 * - Coerces count fields to numbers (API may return strings).
 * - Clamps result to 0–1 so bad data (e.g. completed > total) never shows > 100%.
 * - When we have step or task counts, progress is derived only from them (never from API status).
 * - When we have no counts (both 0/missing), we do not assume 100% from API status so the report
 *   column shows Not Started / In Progress / Complete only from actual progress (0% / <100% / 100%).
 */

const toNumber = (v) => {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {Object} project - project with optional completion_steps_*, tasks_*, status
 * @returns {{ percent: number, progressPercent: number, completedCount: number, totalCount: number }}
 *   percent in 0–1, progressPercent in 0–100; completedCount/totalCount are the pair used for display (steps or tasks).
 */
export function getProjectProgress(project) {
  const empty = { percent: 0, progressPercent: 0, completedCount: 0, totalCount: 0 };
  if (!project || typeof project !== 'object') {
    return empty;
  }

  const completionStepsTotal = toNumber(project.completion_steps_count);
  const completionStepsCompleted = toNumber(project.completion_steps_completed_count);
  const tasksTotal = toNumber(project.tasks_count);
  const tasksCompleted = toNumber(project.tasks_completed_count);

  let percent = 0;
  let completedCount = 0;
  let totalCount = 0;

  if (completionStepsTotal > 0) {
    percent = completionStepsCompleted / completionStepsTotal;
    completedCount = Math.max(0, completionStepsCompleted);
    totalCount = Math.max(0, completionStepsTotal);
  } else if (tasksTotal > 0) {
    percent = tasksCompleted / tasksTotal;
    completedCount = Math.max(0, tasksCompleted);
    totalCount = Math.max(0, tasksTotal);
  }
  // When API omits both step and task counts, percent stays 0 so report shows "Not Started" instead of "Complete"

  percent = Math.min(1, Math.max(0, percent));
  // Cap display counts: no negatives, and completed never exceeds total (e.g. avoid 130/124)
  if (completedCount < 0) completedCount = 0;
  if (totalCount < 0) totalCount = 0;
  if (totalCount > 0 && completedCount > totalCount) completedCount = totalCount;
  const progressPercent = Math.round(percent * 100);

  return { percent, progressPercent, completedCount, totalCount };
}

/**
 * Clamp a 0–100 percentage (e.g. from groupCompletionPercentage) to valid range.
 * @param {number} value
 * @returns {number} value in [0, 100]
 */
export function clampPercentage(value) {
  if (value == null || !Number.isFinite(Number(value))) return 0;
  const n = Number(value);
  return Math.min(100, Math.max(0, n));
}



// WEBPACK FOOTER //
// ./src/helpers/projectProgressUtil.js